const DEFAULT_LOCALE = new Intl.Locale("en-US");
const LOAD_ESTIMATE_DURATION_STEPS_SECONDS = [5, 10, 20, 30, 60] as const;
const MAXIMUM_LOAD_ESTIMATE_DURATION_SECONDS = 10 * 60;

const resolveLocale = (locale?: string | Intl.Locale) => {
  if (!locale) {
    return DEFAULT_LOCALE.baseName;
  }

  if (typeof locale === "string") {
    return locale;
  }

  return locale.baseName;
};

export const formatDurationShort = (seconds: number, locale?: string | Intl.Locale) => {
  const sanitizedSeconds =
    typeof seconds === "number" && Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;

  const localeName = resolveLocale(locale);
  const secondFormatter = new Intl.NumberFormat(localeName, {
    style: "unit",
    unit: "second",
    unitDisplay: "narrow",
  });

  if (sanitizedSeconds < 60) {
    return secondFormatter.format(sanitizedSeconds);
  }

  const minuteFormatter = new Intl.NumberFormat(localeName, {
    style: "unit",
    unit: "minute",
    unitDisplay: "narrow",
  });

  const minutes = Math.floor(sanitizedSeconds / 60);
  const remainingSeconds = sanitizedSeconds % 60;

  return `${minuteFormatter.format(minutes)} ${secondFormatter.format(remainingSeconds)}`;
};

export const formatDurationLong = (seconds: number, locale?: string | Intl.Locale) => {
  const sanitizedSeconds =
    typeof seconds === "number" && Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;

  const localeName = resolveLocale(locale);
  const secondFormatter = new Intl.NumberFormat(localeName, {
    style: "unit",
    unit: "second",
    unitDisplay: "long",
  });

  if (sanitizedSeconds < 60) {
    return secondFormatter.format(sanitizedSeconds);
  }

  const minuteFormatter = new Intl.NumberFormat(localeName, {
    style: "unit",
    unit: "minute",
    unitDisplay: "long",
  });
  const minutes = Math.floor(sanitizedSeconds / 60);
  const remainingSeconds = sanitizedSeconds % 60;
  const formattedMinutes = minuteFormatter.format(minutes);

  return remainingSeconds === 0
    ? formattedMinutes
    : `${formattedMinutes} ${secondFormatter.format(remainingSeconds)}`;
};

/** Formats loading expectations compactly: seconds below one minute, rounded whole minutes after. */
export const formatLoadEstimateDuration = (milliseconds: number, locale?: string | Intl.Locale) => {
  const seconds =
    typeof milliseconds === "number" && Number.isFinite(milliseconds)
      ? Math.max(1, Math.ceil(milliseconds / 1000))
      : 1;

  const cappedSeconds = Math.min(seconds, MAXIMUM_LOAD_ESTIMATE_DURATION_SECONDS);
  const steppedSeconds = LOAD_ESTIMATE_DURATION_STEPS_SECONDS.find((step) => cappedSeconds <= step);

  if (steppedSeconds) {
    return formatDurationLong(steppedSeconds, locale);
  }

  return new Intl.NumberFormat(resolveLocale(locale), {
    style: "unit",
    unit: "minute",
    unitDisplay: "long",
  }).format(Math.max(1, Math.ceil(cappedSeconds / 60)));
};

export const formatDurationClock = (seconds: number, locale?: string | Intl.Locale) => {
  const sanitizedSeconds =
    typeof seconds === "number" && Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const localeName = resolveLocale(locale);
  const formatterOptions = {
    style: "unit" as const,
    unitDisplay: "narrow" as const,
    minimumIntegerDigits: 2,
    useGrouping: false,
  };
  const secondFormatter = new Intl.NumberFormat(localeName, {
    ...formatterOptions,
    unit: "second",
  });

  if (sanitizedSeconds < 60) {
    return secondFormatter.format(sanitizedSeconds);
  }

  const minuteFormatter = new Intl.NumberFormat(localeName, {
    ...formatterOptions,
    unit: "minute",
  });
  const minutes = Math.floor(sanitizedSeconds / 60);
  const remainingSeconds = sanitizedSeconds % 60;

  return `${minuteFormatter.format(minutes)} ${secondFormatter.format(remainingSeconds)}`;
};

export const formatMillisecondsShort = (milliseconds: number, locale?: string | Intl.Locale) =>
  formatDurationShort(milliseconds / 1000, locale);
